// src/Components/CommunityPermissionsV2Panel/CommunityPermissionsV2Panel.tsx
//
// DEV-861 [U1] / DEV-854. Community-scope role management UI. Mounted
// inside the CommunityEntitiesTab edit tray (and potentially a community
// settings page in the future). Lets a community admin or scoped sysadmin:
//   - See current grants on this community (user + bundle + 2FA-pending state)
//   - Assign a user a community-scope bundle (Owner / Admin / Member /
//     Content Creator / Rotary-named bundles)
//   - Revoke an existing grant
//
// Backed by the generalized scope-typed endpoints added in DEV-859:
//   - listGrantsForScope("community", communityGuid)
//   - listPermissionSetsForScope("community", communityGuid)
//   - createGrantForScope("community", communityGuid, ...)
//   - revokeGrant(grantGuid)
//
// Auth model: the parent surface (CommunityEntitiesTab) gates rendering
// to admins. The backend also enforces via CallerCanAdminScope. This
// component trusts the parent's gate but defaults canAdmin=true; revokes
// are still server-validated.

import { useCallback, useEffect, useMemo, useState } from "react";
import { permissionsApiV2, type PermissionScopeType } from "../../security/v2/apiClient";
import type {
  PermissionGrantDto,
  PermissionSetDto,
} from "../../security/v2/types";
import { fetchAllSecurityUsers, type SecurityUserResponse } from "../../api/security";
import { normalizeGuid } from "../../utils/guid";

interface Props {
  communityGuid: string;
  /** Whether the parent caller has admin authority. When false, the form
   *  and revoke actions are hidden but the grants list still renders. */
  canAdmin?: boolean;
  /**
   * DEV-892. The acting user's GUID. Used as `created_by` when emitting a
   * community.role_assignment workflow request so the recipient sees who
   * invited them. Optional — falls back to empty string if not supplied.
   */
  currentUserGuid?: string;
  /**
   * DEV-876. Display label rendered in the panel header to signal which
   * entity the panel is operating on. Example: "Roles on Rotary
   * International" or "Roles on Hampton Rotary Club". Optional — falls
   * back to a generic "Roles & Permissions" header when not supplied.
   */
  scopeLabel?: string;
}

interface LoadState {
  loading: boolean;
  error: string | null;
  bundles: PermissionSetDto[];
  grants: PermissionGrantDto[];
  users: SecurityUserResponse[];
}

const initialState: LoadState = {
  loading: true,
  error: null,
  bundles: [],
  grants: [],
  users: [],
};

const SCOPE: PermissionScopeType = "community";

export default function CommunityPermissionsV2Panel({
  communityGuid,
  canAdmin = true,
  currentUserGuid: currentUserGuidProp = "",
  scopeLabel,
}: Props) {
  const [state, setState] = useState<LoadState>(initialState);
  const [busy, setBusy] = useState(false);

  // Assignment form state
  const [pickUserGuid, setPickUserGuid] = useState<string>("");
  const [pickBundleGuid, setPickBundleGuid] = useState<string>("");
  const [userSearch, setUserSearch] = useState("");
  const [userPickerOpen, setUserPickerOpen] = useState(false);
  // DEV-892. Optional personal message attached to the role-invitation
  // workflow request. Renders on the recipient's Action Center card.
  const [inviteMessage, setInviteMessage] = useState<string>("");

  const load = useCallback(async () => {
    if (!communityGuid) return;
    setState((s) => ({ ...s, loading: true, error: null }));
    try {
      const [bundles, grants, users] = await Promise.all([
        permissionsApiV2.listPermissionSetsForScope(SCOPE, communityGuid),
        permissionsApiV2.listGrantsForScope(SCOPE, communityGuid),
        fetchAllSecurityUsers(),
      ]);
      setState({ loading: false, error: null, bundles, grants, users });
    } catch (err: any) {
      setState((s) => ({
        ...s,
        loading: false,
        error: String(err?.message ?? err),
      }));
    }
  }, [communityGuid]);

  useEffect(() => { load(); }, [load]);

  // Lookups
  const userByGuid = useMemo(() => {
    const m = new Map<string, SecurityUserResponse>();
    for (const u of state.users) m.set(normalizeGuid(u.userGuid), u);
    return m;
  }, [state.users]);

  const bundleByGuid = useMemo(() => {
    const m = new Map<string, PermissionSetDto>();
    for (const b of state.bundles) m.set(normalizeGuid(b.permissionSetGuid), b);
    return m;
  }, [state.bundles]);

  // Filter users by search query (client-side)
  const userOptions = useMemo(() => {
    const q = userSearch.trim().toLowerCase();
    let pool = state.users;
    if (q) {
      pool = pool.filter((u) =>
        (u.displayName ?? u.name ?? "").toLowerCase().includes(q) ||
        (u.email ?? "").toLowerCase().includes(q)
      );
    }
    return pool
      .slice()
      .sort((a, b) =>
        (a.displayName || a.name || a.email || "").localeCompare(
          b.displayName || b.name || b.email || "",
          undefined,
          { numeric: true, sensitivity: "base" }
        )
      )
      .slice(0, 50);
  }, [state.users, userSearch]);

  const handleAssign = async () => {
    if (!canAdmin || !pickUserGuid || !pickBundleGuid) return;
    setBusy(true);
    try {
      const grant = await permissionsApiV2.createGrantForScope(SCOPE, communityGuid, {
        permissionSetGuid: pickBundleGuid,
        userGuid: pickUserGuid,
      });

      // DEV-892. If the grant came back as 'pending' (non-self assignment),
      // emit a workflow request so the recipient sees a community.role_assignment
      // notification in their Action Center with accept/decline options.
      // Self-assignments are auto-active by the backend; no notification needed.
      if (grant.status === "pending") {
        try {
          const { addOrUpdateWorkflow, submitRequest } = await import("../../workflow");
          const userName = userByGuid.get(normalizeGuid(pickUserGuid))?.displayName
            ?? userByGuid.get(normalizeGuid(pickUserGuid))?.name
            ?? "User";
          const bundleName = bundleByGuid.get(normalizeGuid(pickBundleGuid))?.name ?? "Role";
          const now = new Date();
          const trimmedMessage = inviteMessage.trim();
          // DEV-1017: must be a real UUID. The backend's WorkflowRequest
          // model types WorkflowGuid as System.Guid; JSON deserialization
          // rejects non-Guid strings (e.g. the legacy `wf_TIMESTAMP_HEX`
          // shape) with "could not be converted to System.Guid". Use
          // crypto.randomUUID with a Guid-format fallback for old browsers.
          const makeGuid = (): string => {
            try {
              const c: any = (globalThis as any).crypto;
              if (c?.randomUUID) return String(c.randomUUID());
            } catch {
              /* fall through */
            }
            // RFC 4122 v4 fallback — only used on browsers without crypto.randomUUID.
            return "xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx".replace(/[xy]/g, (c) => {
              const r = (Math.random() * 16) | 0;
              const v = c === "x" ? r : (r & 0x3) | 0x8;
              return v.toString(16);
            });
          };
          const draft = {
            workflow_guid: makeGuid(),
            target_type: "community" as const,
            target_guid: communityGuid,
            action_type: "community.role_assignment",
            title: `${bundleName} invitation`,
            description: trimmedMessage
              ? `${trimmedMessage}\n\n— You've been invited to take the ${bundleName} role.`
              : `You've been invited to take the ${bundleName} role.`,
            payload: {
              community_guid: communityGuid,
              user_guid: pickUserGuid,
              permission_set_guid: pickBundleGuid,
              grant_guid: grant.grantGuid,
              role: bundleName,
              user_display_name: userName,
              // DEV-892. Optional inviter message; rendered prominently on
              // the recipient's Action Center card. Empty string when not set.
              custom_message: trimmedMessage,
            },
            status: "draft" as const,
            version: 1,
            created_by: String(currentUserGuidProp || ""),
            created_at: now.toISOString(),
            updated_at: now.toISOString(),
            assigned_to_guids: [pickUserGuid],
            history: [{ action: "draft" as const, by: String(currentUserGuidProp || ""), at: now.toISOString() }],
          };
          // DEV-1017 followup: only one write to the store + API. The
          // previous pattern fired addOrUpdateWorkflow(draft) then
          // immediately addOrUpdateWorkflow(submitted) — two POSTs with
          // the same workflow_guid in quick succession raced against the
          // backend's check-then-insert upsert (FirstOrDefaultAsync sees
          // null because the first transaction hasn't committed yet,
          // then INSERT trips the unique constraint → 500). The draft
          // state was never user-visible because submit fires inline.
          // Compute submitted directly and write once.
          const submitted = submitRequest(draft as any, String(currentUserGuidProp || ""));
          addOrUpdateWorkflow(submitted);
        } catch (wfErr) {
          // Workflow request failure is non-fatal — the grant is still pending,
          // an admin can manually resend the notification or accept on the
          // user's behalf via sysadmin path. Log so it's visible.
          console.warn("[CommunityPermissionsV2Panel] workflow emit failed:", wfErr);
        }
      }

      setPickUserGuid("");
      setPickBundleGuid("");
      setUserSearch("");
      setInviteMessage(""); // DEV-892
      await load();
    } catch (err: any) {
      setState((s) => ({ ...s, error: String(err?.message ?? err) }));
    } finally {
      setBusy(false);
    }
  };

  const handleRevoke = async (g: PermissionGrantDto) => {
    if (!canAdmin) return;
    const userName = userByGuid.get(normalizeGuid(g.userGuid ?? ""))?.displayName ?? "this user";
    const bundleName = bundleByGuid.get(normalizeGuid(g.permissionSetGuid))?.name ?? "this role";
    if (!confirm(`Revoke ${bundleName} from ${userName}?`)) return;
    setBusy(true);
    try {
      await permissionsApiV2.revokeGrant(g.grantGuid);
      await load();
    } catch (err: any) {
      setState((s) => ({ ...s, error: String(err?.message ?? err) }));
    } finally {
      setBusy(false);
    }
  };

  // ── Render ──

  if (state.loading) {
    return (
      <div style={{ padding: 12, opacity: 0.6, fontSize: 12 }}>
        Loading roles…
      </div>
    );
  }

  // Filter to user-direct grants (slot grants are project-scope only).
  const userGrants = state.grants.filter((g) => g.userGuid && g.userGuid !== "00000000-0000-0000-0000-000000000000");

  return (
    <div
      style={{
        marginBlockStart: 12,
        paddingBlockStart: 12,
        borderBlockStart: "1px solid var(--border-subtle)",
      }}
    >
      <div
        style={{
          fontSize: 12,
          fontWeight: 600,
          color: "var(--text-secondary)",
          marginBlockEnd: 8,
        }}
      >
        {scopeLabel ? `Roles on ${scopeLabel}` : "Roles & Permissions"}{" "}
        {userGrants.length > 0 && (
          <span style={{ opacity: 0.6, fontWeight: 400 }}>({userGrants.length})</span>
        )}
      </div>

      {state.error && (
        <div
          style={{
            padding: "6px 10px",
            background: "var(--error-bg)",
            border: "1px solid var(--error-border)",
            borderRadius: 6,
            color: "var(--color-error)",
            fontSize: 12,
            marginBlockEnd: 8,
          }}
        >
          {state.error}
        </div>
      )}

      {/* Existing grants list */}
      {userGrants.length === 0 ? (
        <div
          style={{
            fontSize: 11,
            opacity: 0.55,
            fontStyle: "italic",
            marginBlockEnd: 12,
          }}
        >
          No roles assigned. Use the form below to assign an Owner, Admin, or
          other role to a user on this community.
        </div>
      ) : (
        <div
          style={{
            display: "flex",
            flexDirection: "column",
            gap: 6,
            marginBlockEnd: 12,
          }}
        >
          {userGrants.map((g) => {
            const u = userByGuid.get(normalizeGuid(g.userGuid ?? ""));
            const b = bundleByGuid.get(normalizeGuid(g.permissionSetGuid));
            const userLabel = u
              ? `${u.displayName || u.name || "(no name)"} <${u.email}>`
              : g.userGuid;
            const bundleLabel = b?.name ?? "(unknown bundle)";
            const requires2fa = b?.requires2fa === true;
            return (
              <div
                key={g.grantGuid}
                style={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  padding: "6px 10px",
                  background: "var(--bg-hover)",
                  borderRadius: 6,
                  border: "1px solid var(--border-subtle)",
                  fontSize: 12,
                }}
              >
                <div style={{ flex: 1, minWidth: 0 }}>
                  <span style={{ fontWeight: 600 }}>{bundleLabel}</span>
                  {b?.isScopedSysadmin && (
                    <span
                      style={{
                        marginInlineStart: 6,
                        fontSize: 10,
                        padding: "1px 6px",
                        borderRadius: 4,
                        background: "var(--accent-bg)",
                        color: "var(--accent-text)",
                      }}
                      title="Scoped sysadmin — full authority within this org subtree"
                    >
                      Org Admin
                    </span>
                  )}
                  {requires2fa && (
                    <span
                      style={{
                        marginInlineStart: 6,
                        fontSize: 10,
                        padding: "1px 6px",
                        borderRadius: 4,
                        background: "var(--bg-secondary)",
                        color: "var(--text-tertiary)",
                      }}
                      title="Bundle requires 2FA — grant is dormant until the user enrolls TOTP"
                    >
                      2FA required
                    </span>
                  )}
                  {/* DEV-892 — grant lifecycle status pills */}
                  {g.status === "pending" && (
                    <span
                      style={{
                        marginInlineStart: 6,
                        fontSize: 10,
                        padding: "1px 6px",
                        borderRadius: 4,
                        background: "rgba(251,191,36,0.15)",
                        color: "rgba(252,211,77,1)",
                        border: "1px solid rgba(251,191,36,0.35)",
                      }}
                      title="The recipient has not yet accepted this role invitation. Until they accept, the role is dormant."
                    >
                      pending acceptance
                    </span>
                  )}
                  {g.status === "declined" && (
                    <span
                      style={{
                        marginInlineStart: 6,
                        fontSize: 10,
                        padding: "1px 6px",
                        borderRadius: 4,
                        background: "rgba(248,113,113,0.12)",
                        color: "rgba(252,165,165,1)",
                        border: "1px solid rgba(248,113,113,0.35)",
                      }}
                      title="The recipient declined this role invitation. The grant is preserved for audit."
                    >
                      declined
                    </span>
                  )}
                  <div
                    style={{
                      fontSize: 11,
                      opacity: 0.75,
                      marginBlockStart: 2,
                    }}
                  >
                    {userLabel}
                  </div>
                </div>
                {canAdmin && (
                  <button
                    type="button"
                    onClick={() => handleRevoke(g)}
                    disabled={busy}
                    title="Revoke this role"
                    style={{
                      border: "1px solid var(--error-border)",
                      background: "transparent",
                      color: "var(--color-error)",
                      borderRadius: 6,
                      padding: "2px 10px",
                      fontSize: 11,
                      cursor: "pointer",
                      whiteSpace: "nowrap",
                      marginInlineStart: 8,
                    }}
                  >
                    Revoke
                  </button>
                )}
              </div>
            );
          })}
        </div>
      )}

      {/* Assign form */}
      {canAdmin && (() => {
        const selectedUser = pickUserGuid
          ? userByGuid.get(normalizeGuid(pickUserGuid))
          : null;
        const labelStyle: React.CSSProperties = {
          fontSize: 11,
          fontWeight: 600,
          color: "var(--text-tertiary)",
          letterSpacing: "0.02em",
          marginBlockEnd: 4,
        };
        const fieldStyle: React.CSSProperties = {
          width: "100%",
          padding: "6px 10px",
          fontSize: 12,
          border: "1px solid var(--border-default)",
          borderRadius: 6,
          background: "var(--bg-primary)",
          color: "var(--text-primary)",
          boxSizing: "border-box",
        };
        return (
          <div
            style={{
              display: "flex",
              flexDirection: "column",
              gap: 10,
              padding: 12,
              background: "var(--bg-secondary)",
              borderRadius: 6,
              border: "1px solid var(--border-subtle)",
            }}
          >
            <div style={{ fontSize: 12, fontWeight: 600, color: "var(--text-secondary)" }}>
              Assign a role
            </div>

            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
              {/* User combobox — search-as-you-type */}
              <div style={{ position: "relative" }}>
                <div style={labelStyle}>User</div>
                {selectedUser ? (
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: 6,
                      padding: "6px 10px",
                      background: "var(--bg-hover)",
                      border: "1px solid var(--border-subtle)",
                      borderRadius: 6,
                      fontSize: 12,
                    }}
                  >
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ fontWeight: 500 }}>
                        {selectedUser.displayName || selectedUser.name || "(no name)"}
                      </div>
                      {selectedUser.email && (
                        <div style={{ fontSize: 11, opacity: 0.7 }}>{selectedUser.email}</div>
                      )}
                    </div>
                    <button
                      type="button"
                      onClick={() => { setPickUserGuid(""); setUserSearch(""); }}
                      title="Clear selection"
                      disabled={busy}
                      style={{
                        border: "none",
                        background: "none",
                        color: "var(--text-tertiary)",
                        cursor: "pointer",
                        fontSize: 14,
                        lineHeight: 1,
                        padding: "0 4px",
                      }}
                    >
                      ×
                    </button>
                  </div>
                ) : (
                  <>
                    <input
                      type="text"
                      value={userSearch}
                      onChange={(e) => { setUserSearch(e.target.value); setUserPickerOpen(true); }}
                      onFocus={() => setUserPickerOpen(true)}
                      onBlur={() => setTimeout(() => setUserPickerOpen(false), 150)}
                      placeholder="Type a name or email…"
                      disabled={busy}
                      style={fieldStyle}
                    />
                    {userPickerOpen && userSearch.trim() !== "" && (
                      <div
                        style={{
                          position: "absolute",
                          insetInlineStart: 0,
                          insetInlineEnd: 0,
                          top: "100%",
                          marginBlockStart: 2,
                          background: "var(--bg-secondary)",
                          border: "1px solid var(--border-default)",
                          borderRadius: 6,
                          maxHeight: 240,
                          overflowY: "auto",
                          zIndex: 1000,
                          boxShadow: "0 4px 12px rgba(0,0,0,0.18)",
                        }}
                      >
                        {userOptions.length === 0 ? (
                          <div style={{ padding: "6px 10px", fontSize: 11, opacity: 0.55, fontStyle: "italic" }}>
                            No users match.
                          </div>
                        ) : (
                          userOptions.map((u) => (
                            <div
                              key={u.userGuid}
                              onMouseDown={() => {
                                setPickUserGuid(u.userGuid);
                                setUserSearch("");
                                setUserPickerOpen(false);
                              }}
                              style={{
                                padding: "6px 10px",
                                cursor: "pointer",
                                fontSize: 12,
                                borderBlockEnd: "1px solid var(--border-subtle)",
                              }}
                              onMouseEnter={(ev) => { (ev.currentTarget as HTMLDivElement).style.background = "var(--bg-hover)"; }}
                              onMouseLeave={(ev) => { (ev.currentTarget as HTMLDivElement).style.background = ""; }}
                            >
                              <div style={{ fontWeight: 500 }}>
                                {u.displayName || u.name || "(no name)"}
                              </div>
                              {u.email && (
                                <div style={{ fontSize: 11, opacity: 0.7, marginBlockStart: 1 }}>
                                  {u.email}
                                </div>
                              )}
                            </div>
                          ))
                        )}
                      </div>
                    )}
                  </>
                )}
              </div>

              {/* Role select with label */}
              <div>
                <div style={labelStyle}>Role</div>
                <select
                  value={pickBundleGuid}
                  onChange={(e) => setPickBundleGuid(e.target.value)}
                  disabled={busy}
                  style={fieldStyle}
                >
                  <option value="">— Choose a role —</option>
                  {state.bundles.map((b) => (
                    <option key={b.permissionSetGuid} value={b.permissionSetGuid}>
                      {b.name}
                      {b.isScopedSysadmin ? " (Org Admin)" : ""}
                      {b.requires2fa ? " · 2FA" : ""}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            {/* DEV-892. Optional inviter message. Only shown for non-self
                assignments (since self-assigning doesn't generate a notification). */}
            {pickUserGuid && pickUserGuid !== currentUserGuidProp && (
              <div style={{ marginBlockStart: 8 }}>
                <div style={labelStyle}>
                  Personal message (optional)
                  <span style={{ fontWeight: 400, opacity: 0.7, marginInlineStart: 6 }}>
                    Will appear on the recipient's invitation in their Action Center.
                  </span>
                </div>
                <textarea
                  value={inviteMessage}
                  onChange={(e) => setInviteMessage(e.target.value)}
                  disabled={busy}
                  rows={3}
                  maxLength={500}
                  placeholder="e.g., Hi Martha — we're rolling out Life4Earth across the district and I'd love your help managing the Pawtucket page. Let me know if you have questions."
                  style={{
                    ...fieldStyle,
                    resize: "vertical",
                    minHeight: 64,
                    fontFamily: "inherit",
                    lineHeight: 1.4,
                  }}
                />
                <div style={{ fontSize: 10, opacity: 0.6, marginBlockStart: 2, textAlign: "end" }}>
                  {inviteMessage.length} / 500
                </div>
              </div>
            )}

            <div style={{ display: "flex", justifyContent: "flex-end", marginBlockStart: 4 }}>
              <button
                type="button"
                onClick={handleAssign}
                disabled={busy || !pickUserGuid || !pickBundleGuid}
                style={{
                  padding: "6px 16px",
                  fontSize: 12,
                  fontWeight: 600,
                  border: "1px solid var(--accent-border)",
                  background: pickUserGuid && pickBundleGuid ? "var(--accent-bg)" : "var(--bg-hover)",
                  color: pickUserGuid && pickBundleGuid ? "var(--accent-text)" : "var(--text-tertiary)",
                  borderRadius: 6,
                  cursor: busy ? "wait" : (pickUserGuid && pickBundleGuid ? "pointer" : "not-allowed"),
                  whiteSpace: "nowrap",
                  opacity: busy ? 0.7 : 1,
                }}
              >
                {busy ? "Assigning…" : "Assign role"}
              </button>
            </div>
          </div>
        );
      })()}
    </div>
  );
}
